import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import {useMediaQuery} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import React, {useCallback, useEffect, useState} from "react";
import {
    HIDE,
    MEDIA_PLAYING_NAVIGATE,
    SHOW,
    useSearchResultDetailsDispatch,
    useSearchResultDetailsState,
    useSearchResultDetailsTranslation
} from "../search-result-details/SearchResultDetailsContext";
import {CONVERSION_UNKNOWN, useObjectConversionDispatch} from "./ObjectConversionContext";
import useDeepCompareEffect from "use-deep-compare-effect";
import {useSearchState} from "../search/SearchContext";

const navigateArrow = {
    position: "absolute",
    cursor: "pointer",
    // Stack above the play button (zIndex 10 in ObjectMediaPlayIcon), so the arrows win
    // wherever the two meet in small-screen landscape (DE-1637).
    zIndex: 11,
};

// Keeps the arrows' look while making them focusable, keyboard-operable buttons.
const navigateButton = {
    padding: 0,
    borderRadius: 0,
};

const navigateIcon = {
    color: "white",
    backgroundColor: "rgba(0,0,0,0.4)"
};


/**
 * Custom hook to manage navigation between objects in a view.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {Object} params.model - The current object model.
 * @param {boolean} params.smallScreen - Flag indicating if the screen is small.
 * @param {Function} params.openDialogCallback - Callback to open or close a dialog.
 *
 * @returns {Object} - Functions and state to manage navigation.
 */
export const useObjectViewPreviousNext = ({
                                              model,
                                              smallScreen,
                                              openDialogCallback
                                          }) => {

    const {results, searching} = useSearchState();
    const objectConversionDispatch = useObjectConversionDispatch();
    const searchResultDetailsDispatch = useSearchResultDetailsDispatch();
    const {standalone} = useSearchResultDetailsState();
    const t = useSearchResultDetailsTranslation();

    const [previous, setPrevious] = useState("");
    const [next, setNext] = useState("");
    const [showNavigation, setShowNavigation] = useState(true);

    // Detects portrait mode on smaller screens.
    const isPortrait = useMediaQuery("(orientation:portrait)");

    // Position navigation arrows in the vertical direction,
    // based on the current screen orientation on smaller screens.
    let navigateArrowTopPosition = isPortrait ? "12%" : "50%";

    /**
     * Navigate to previous/next handler.
     * @param idx
     */
    const handleOnNavigate = (idx) => {
        searchResultDetailsDispatch({
            type: MEDIA_PLAYING_NAVIGATE,
            playing: false
        });
        objectConversionDispatch({type: CONVERSION_UNKNOWN});
        searchResultDetailsDispatch({
            type: SHOW,
            model: results.models.find((m) => m.uniqueId === idx),
        });
    };

    /**
     * The previous button (left arrow).
     * @returns {JSX.Element|string}
     */
    const getPreviousButton = () => {
        if (showNavigation && previous) {
            if (!smallScreen) {
                return (<Box sx={{position: "fixed", left: 0, cursor: "pointer"}}>
                    <IconButton
                        sx={navigateButton}
                        onClick={() => handleOnNavigate(previous)}
                        aria-label={t("previousObject", "Forrige objekt")}
                    >
                        <NavigateBeforeIcon
                            sx={{
                                fontSize: 120,
                                ...navigateIcon
                            }}
                        />
                    </IconButton>
                </Box>);
            } else if (smallScreen) {
                return <Box
                    sx={{
                        ...navigateArrow,
                        top: navigateArrowTopPosition,
                    }}
                >
                    <IconButton
                        sx={navigateButton}
                        onClick={() => handleOnNavigate(previous)}
                        aria-label={t("previousObject", "Forrige objekt")}
                    >
                        <NavigateBeforeIcon sx={{fontSize: "40px", ...navigateIcon}}/>
                    </IconButton>
                </Box>;
            }
        } else {
            return '';
        }
    };

    /**
     * The next button (right arrow).
     * @returns {JSX.Element|string}
     */
    const getNextButton = () => {
        if (showNavigation && next) {
            if (!smallScreen) {
                return (<Box sx={{position: "fixed", right: 0, cursor: "pointer"}}>
                    <IconButton
                        sx={navigateButton}
                        onClick={() => handleOnNavigate(next)}
                        aria-label={t("nextObject", "Neste objekt")}
                    >
                        <NavigateNextIcon
                            sx={{
                                fontSize: 120,
                                ...navigateIcon
                            }}
                        />
                    </IconButton>
                </Box>);
            } else if (smallScreen) {
                return <Box
                    sx={{
                        ...navigateArrow,
                        top: navigateArrowTopPosition,
                        right: isPortrait ? 0 : "70%",
                    }}
                >
                    <IconButton
                        sx={navigateButton}
                        onClick={() => handleOnNavigate(next)}
                        aria-label={t("nextObject", "Neste objekt")}
                    >
                        <NavigateNextIcon sx={{fontSize: "40px", ...navigateIcon}}/>
                    </IconButton>
                </Box>;
            }
        } else {
            return '';
        }
    };

    const setPdfOpen = open => {
        if (open) {
            setShowNavigation(false);
        } else {
            setShowNavigation(true);
        }
    };

    /**
     * Handle used to support navigating back/forth with the left- and right arrow keys.
     * @param event
     */
    const keyDownHandler = useCallback(event => {
        if (event.key === 'ArrowLeft' && showNavigation && previous) {
            handleOnNavigate(previous);
        }

        if (event.key === 'ArrowRight' && showNavigation && next) {
            handleOnNavigate(next);
        }
    }, [showNavigation, previous, next, handleOnNavigate]);

    /**
     * Hook adding the event listener used to listen to keystrokes on the left- and right arrow keys.
     */
    useEffect(() => {
        document.addEventListener('keydown', keyDownHandler, false);
        return () => {
            document.removeEventListener('keydown', keyDownHandler, false);
        }
    }, [keyDownHandler]);

    /**
     * Hook used to handle the previous/next navigation.
     */
    useDeepCompareEffect(() => {
        if (standalone) {
            // DE-1719: opened by its URL; the search results (if any) are not its neighbours.
            setPrevious("");
            setNext("");
            return;
        }
        if (searching) {
            return;
        }

        const {uniqueId} = model;

        // NOTE: using results.models.length to trigger the effect rather than results.count,
        //      because results.count may not always be updated correctly when in projects context.

        if (results.models.length === 0) {
            openDialogCallback(false);
            searchResultDetailsDispatch({type: HIDE});
        } else if (uniqueId && results.models.length > 0) {
            const objectExists = Object.entries(results.modelsOrder).find(e => e[1] === uniqueId);
            let currentIdx = objectExists === undefined ? 0 : parseInt(objectExists[0]);
            setPrevious(
                results.modelsOrder[currentIdx - 1]
                    ? results.modelsOrder[currentIdx - 1]
                    : ""
            );
            setNext(
                results.modelsOrder[currentIdx + 1]
                    ? results.modelsOrder[currentIdx + 1]
                    : ""
            );
        }
    }, [standalone, searching, model.uniqueId, results.modelsOrder, results.models.length]);


    return {
        getPreviousButton,
        getNextButton,
        setShowNavigation,
        setPdfOpen
    };

};